import React, { useEffect, useState } from "react";

import UnderlineHeading from "../underlineHeading";
import coursePlanService from "../../services/v2/coursePlan.service";
import { HolidayReplacementBody, ScheduleHolidayDTO } from "../../models/CoursePlanDTO";
import { getErrorMessage } from "../../common";
import { fechaBonita } from "../../utils/fechaBonita";
import { FechaDMY, HoraAmPm } from "../fechas/camposFecha";

import "../../navigation/v2/managment/enrollmentUpdates/styles.css";

/*
	Asuetos de un horario dentro de su ficha (FASE 3, 2026-10-01): los del plantel que le tocan y los propios.
	Al poner uno se puede elegir "reponer el <fecha> en <aula>": la clase de ese día se cambia por esa.
*/

export interface AulaOpcion { id: number; name: string; }

const S = {
	title: "Asuetos",
	empty: "Sin asuetos.",
	campus: "Plantel",
	own: "Este horario",
	noClass: "Sin clase ese día",
	recovers: "Se repone el",
	in: "en",
	add: "Agregar Asueto",
	date: "Fecha",
	reason: "Motivo",
	recover: "Reponer",
	recoverDate: "El",
	time: "Hora",
	classroom: "Aula",
	sameClassroom: "La del horario",
	remove: "Quitar",
	removeRecovery: "Quitar reposición",
	back: "Volver",
	save: "Guardar"
};

const horaBonita = (hhmm: string | null | undefined): string => {
	const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
	if (!m) return "";
	const h = Number(m[1]);
	return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? "am" : "pm"}`;
};

/** Fecha, hora y aula de una reposición. */
const ReposicionCampos: React.FC<{ value: HolidayReplacementBody; aulas: AulaOpcion[]; onChange: (v: HolidayReplacementBody) => void }> = ({ value, aulas, onChange }) => (
	<>
		<div className="wau-campo"><span className="wau-lab">{S.recoverDate}</span>
			<FechaDMY className="wau-entrada" value={value.date} onChange={v => onChange({ ...value, date: v })} />
		</div>
		<div className="wau-campo"><span className="wau-lab">{S.time}</span>
			<HoraAmPm value={value.time || ""} onChange={v => onChange({ ...value, time: v || undefined })} />
		</div>
		<label className="wau-campo"><span className="wau-lab">{S.classroom}</span>
			<select className="wau-entrada" value={value.classroomId ?? ""} onChange={e => onChange({ ...value, classroomId: Number(e.target.value) || undefined })}>
				<option value="">{S.sameClassroom}</option>
				{aulas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
			</select>
		</label>
	</>
);

const AsuetosHorario: React.FC<{ campusId: number; scheduleId: number; aulas: AulaOpcion[]; puedeEscribir: boolean }> = ({ campusId, scheduleId, aulas, puedeEscribir }) => {
	const [lista, setLista] = useState<ScheduleHolidayDTO[] | null>(null);
	const [tick, setTick] = useState<number>(0);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [fecha, setFecha] = useState<string>("");
	const [motivo, setMotivo] = useState<string>("");
	const [repone, setRepone] = useState<boolean>(false);
	const [reposicion, setReposicion] = useState<HolidayReplacementBody>({ date: "" });
	//Reponer un asueto ya puesto (también los del plantel)
	const [editando, setEditando] = useState<number | null>(null);
	const [repEdit, setRepEdit] = useState<HolidayReplacementBody>({ date: "" });

	useEffect(() => {
		let vigente = true;
		coursePlanService.getScheduleHolidays(campusId, scheduleId)
			.then(r => vigente && setLista(Array.isArray(r.data?.holidays) ? r.data.holidays : []))
			.catch(e => { if (vigente) { setLista([]); setError(getErrorMessage(e)); } });
		return () => { vigente = false; };
	}, [campusId, scheduleId, tick]);

	const hacer = async (fn: () => Promise<any>, after?: () => void) => {
		setBusy(true);
		setError(null);
		try {
			await fn();
			if (after) after();
			setTick(t => t + 1);
		} catch (e) {
			setError(getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	const agregar = () => hacer(
		() => coursePlanService.addScheduleHoliday(campusId, { scheduleId, date: fecha, name: motivo.trim(), replacement: repone && reposicion.date ? reposicion : undefined }),
		() => { setFecha(""); setMotivo(""); setRepone(false); setReposicion({ date: "" }); }
	);

	return (
		<div style={{ padding: "0px 15px 15px 15px" }}>
			<UnderlineHeading name={S.title} />
			<div className="wau" style={{ padding: 0 }}>
				{error ? <div className="wau-aviso mal" role="status"><span>{error}</span><button type="button" aria-label={S.back} onClick={() => setError(null)}>×</button></div> : null}
				{
					lista === null ? <div className="wau-sec">…</div> : lista.length === 0 ? <div className="wau-sec" style={{ marginBottom: 10 }}>{S.empty}</div> :
						<div className="wau-tabla" style={{ marginBottom: 10 }}>
							<table>
								<tbody>
									{lista.map(h => {
										const rep = h.forSchedule?.replacement || null;
										return (
											<React.Fragment key={h.id}>
												<tr>
													<td className="wau-num" style={{ whiteSpace: "nowrap" }}>{fechaBonita(h.date)}</td>
													<td>{h.name}<div><span className={"wau-pastilla" + (h.scope === "schedule" ? " acento" : "")}>{h.scope === "schedule" ? S.own : S.campus}</span></div></td>
													<td style={{ fontSize: 13.5 }}>
														{
															!h.forSchedule ? <span className="wau-sec">{S.noClass}</span> : rep
																? <>{S.recovers} <b className="wau-num">{fechaBonita(rep.date)} {horaBonita(rep.time)}</b>{rep.classroomName ? ` ${S.in} ${rep.classroomName}` : ""}</>
																: <span className="wau-sec">{h.forSchedule.modeLabel}{h.forSchedule.movedClasses ? ` · ${h.forSchedule.movedClasses}` : ""}</span>
														}
													</td>
													<td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
														{
															!puedeEscribir || !h.forSchedule ? null : rep
																? <button type="button" className="wau-btn s mini" disabled={busy} onClick={() => hacer(() => coursePlanService.removeReplacement(campusId, h.id, scheduleId))}>{S.removeRecovery}</button>
																: <button type="button" className="wau-btn s mini" disabled={busy} onClick={() => { setEditando(h.id); setRepEdit({ date: "" }); }}>{S.recover}</button>
														}{" "}
														{
															!puedeEscribir || h.scope !== "schedule" ? null :
																<button type="button" className="wau-btn s mini" disabled={busy} onClick={() => hacer(() => coursePlanService.deleteHoliday(campusId, h.id))}>{S.remove}</button>
														}
													</td>
												</tr>
												{
													editando !== h.id ? null :
														<tr><td colSpan={4}>
															<div className="wau-fila">
																<ReposicionCampos value={repEdit} aulas={aulas} onChange={setRepEdit} />
																<button type="button" className="wau-btn s mini" disabled={busy} onClick={() => setEditando(null)}>{S.back}</button>
																<button type="button" className="wau-btn p mini" disabled={busy || !repEdit.date}
																	onClick={() => hacer(() => coursePlanService.setReplacement(campusId, h.id, scheduleId, repEdit), () => setEditando(null))}>{S.save}</button>
															</div>
														</td></tr>
												}
											</React.Fragment>
										);
									})}
								</tbody>
							</table>
						</div>
				}
				{
					!puedeEscribir ? null :
						<div className="wau-fila">
							<div className="wau-campo"><span className="wau-lab">{S.date}</span>
								<FechaDMY className="wau-entrada" value={fecha} onChange={setFecha} />
							</div>
							<label className="wau-campo" style={{ flex: "1 1 180px" }}><span className="wau-lab">{S.reason}</span>
								<input className="wau-entrada" type="text" maxLength={120} value={motivo} onChange={e => setMotivo(e.target.value)} />
							</label>
							<label className="wau-opcion" style={{ gridTemplateColumns: "22px auto", alignItems: "center", padding: "6px 10px" }}>
								<input type="checkbox" checked={repone} onChange={e => setRepone(e.target.checked)} style={{ marginTop: 0, gridRow: "auto" }} />
								<span className="t">{S.recover}</span>
							</label>
							{repone ? <ReposicionCampos value={reposicion} aulas={aulas} onChange={setReposicion} /> : null}
							<button type="button" className="wau-btn p" disabled={busy || !fecha || motivo.trim().length < 2 || (repone && !reposicion.date)} onClick={agregar}>{S.add}</button>
						</div>
				}
			</div>
		</div>
	);
};

export default AsuetosHorario;
